import { useState } from 'react'
import { Alert, Box, Button, Typography } from '@mui/material'
import { ChevronDown, ChevronRight, CircleAlert, CircleCheck, CircleHelp, ExternalLink, Sprout } from 'lucide-react'
import { tokens, useNavigate } from '@agribot/polygon-checks-ui'
import type { SupplySummary } from '@agribot/sourcing-model'
import { useOriginSupply, useSupplyPlots } from '../hooks'
import type { PlotStatus } from '../types'

export const PLOT_COLOUR: Record<PlotStatus, string> = {
  passed: tokens.color.brandGreen700, flagged: tokens.color.warning, rejected: tokens.color.danger, excluded: tokens.color.ink400,
}
const LABEL: Record<PlotStatus, string> = { passed: 'Passed', flagged: 'Flagged', rejected: 'Rejected', excluded: 'Excluded' }
const t = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v.toLocaleString('en-GB', { maximumFractionDigits: v < 10 ? 1 : 0 })} t`)
const ha = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v.toLocaleString('en-GB', { maximumFractionDigits: 2 })} ha`)

/** Passed / flagged / rejected as one thin bar, with the rejected count in words beside it. */
export function PlotBar({ passed, flagged, rejected, total, width = 90 }: { passed: number; flagged: number; rejected: number; total: number; width?: number }) {
  const w = (n: number) => `${total ? (n / total) * 100 : 0}%`
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ display: 'flex', width, height: 6, borderRadius: 999, overflow: 'hidden', backgroundColor: tokens.color.ink100 }}>
        <Box sx={{ width: w(passed), backgroundColor: PLOT_COLOUR.passed }} />
        <Box sx={{ width: w(flagged), backgroundColor: PLOT_COLOUR.flagged }} />
        <Box sx={{ width: w(rejected), backgroundColor: PLOT_COLOUR.rejected }} />
      </Box>
      {rejected > 0 && <Box component="span" sx={{ font: `500 12px ${tokens.font.sans}`, color: tokens.color.danger, whiteSpace: 'nowrap' }}>{rejected} rejected</Box>}
    </Box>
  )
}

/** One line under an origin: what is behind it, and a way in. */
export function SupplySummaryLine({ supply, open, onToggle }: { supply: SupplySummary; open: boolean; onToggle: () => void }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap', font: `400 12px ${tokens.font.sans}`, color: 'text.secondary' }}>
      <Sprout size={13} strokeWidth={1.75} />
      <span>
        {supply.organisations} cooperative{supply.organisations === 1 ? '' : 's'} · {supply.farmers} farmers · {supply.plots} plots · {ha(supply.areaHa)}
        {supply.yieldT ? ` · produces ${t(supply.yieldT)} a year (declared)` : ''}
      </span>
      <PlotBar passed={supply.passed} flagged={supply.flagged} rejected={supply.rejected} total={supply.plots} width={70} />
      <Box component="button" type="button" onClick={onToggle} aria-expanded={open}
        sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', color: tokens.color.brandTeal, font: `500 12px ${tokens.font.sans}`, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Farms
      </Box>
      {supply.capacity && <Box sx={{ flexBasis: '100%' }}><CapacityLine supply={supply} /></Box>}
    </Box>
  )
}

/**
 * Whether the origin's farms could produce what it is claimed to supply:
 * their declared harvest times each ingredient's conversion factor, against
 * the campaign's tonnes from the origin. Says why when it cannot judge.
 */
export function CapacityLine({ supply }: { supply: SupplySummary }) {
  const c = supply.capacity!
  const style = { display: 'inline-flex', alignItems: 'flex-start', gap: 1, font: `500 12px ${tokens.font.sans}` }
  const detail = c.byIngredient.map((f) => `${f.ingredient}: ${t(f.claimedT)} claimed, ${f.capacityT === null ? 'not checked' : `${t(f.capacityT)} possible`}${f.conversionPct ? ` at ${f.conversionPct}%` : ''}`).join(' · ')
  if (c.status === 'over') {
    // Name the parts that are over: with another part unchecked there is no single total to compare.
    const over = c.byIngredient.filter((f) => f.status === 'over')
    const unchecked = c.byIngredient.filter((f) => f.status === 'unknown').map((f) => f.ingredient)
    return (
      <Box component="span" title={detail} sx={{ ...style, color: tokens.color.danger }}>
        <Box sx={{ flex: 'none', mt: '1px' }}><CircleAlert size={13} strokeWidth={2} /></Box>
        <span>
          {over.length
            ? over.map((f) => `${f.ingredient}: ${t(f.claimedT)} claimed, its farms' declared production allows about ${t(f.capacityT)} (at ${f.conversionPct}%)`).join('; ')
            : `Claimed ${t(c.claimedT)} from this origin; its farms' declared production allows about ${t(c.capacityT)}`}.
          {unchecked.length > 0 && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> Not checked: {unchecked.join(', ')} (no conversion factor).</Box>}
        </span>
      </Box>
    )
  }
  if (c.status === 'ok') {
    return (
      <Box component="span" title={detail} sx={{ ...style, color: tokens.color.success }}>
        <CircleCheck size={13} strokeWidth={2} />Within its farms' declared production: {t(c.claimedT)} claimed, about {t(c.capacityT)} possible.
      </Box>
    )
  }
  const missing = c.byIngredient.filter((f) => f.conversionPct === null).map((f) => f.ingredient)
  const why = !supply.plotsWithYield
    ? 'no linked plot declares its production'
    : missing.length ? `no conversion factor set for ${missing.join(', ')}` : 'R&D has not set conversion factors'
  return (
    <Box component="span" title={detail || undefined} sx={{ ...style, color: 'text.secondary' }}>
      <CircleHelp size={13} strokeWidth={2} />Capacity not checked: {why}.
    </Box>
  )
}

/**
 * From an origin down to the polygons: cooperatives, their farmers, a
 * farmer's plots with each one's check result, and the existing plot
 * screen for why a plot failed.
 */
export function SupplyDrill({ originId }: { originId: string }) {
  const { data, isPending, error } = useOriginSupply(originId)
  const [farmer, setFarmer] = useState<string | null>(null)
  if (isPending) return <Typography variant="caption" sx={{ color: 'text.secondary' }}>Loading farms…</Typography>
  if (error || !data) return <Alert severity="error" sx={{ mt: 1 }}>{(error as Error | null)?.message ?? 'The farms could not be loaded.'}</Alert>
  return (
    <Box sx={{ mt: 2, ml: 4, pl: 3, borderLeft: `2px solid ${tokens.color.ink200}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {data.organisations.map((o) => (
        <Box key={o.id}>
          <Typography sx={{ font: `600 13px ${tokens.font.sans}` }}>
            {o.name}{o.code ? <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · {o.code}</Box> : null}
            {o.attributedT !== undefined ? <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}> · {t(o.attributedT)} of this origin’s volume</Box> : null}
          </Typography>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {o.farmers.map((f) => (
              <Box component="li" key={f.id}>
                <Box component="button" type="button" onClick={() => setFarmer(farmer === f.id ? null : f.id)} aria-expanded={farmer === f.id}
                  sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, py: 1, border: 0, background: 'none', cursor: 'pointer', textAlign: 'left', color: 'inherit', font: `400 13px ${tokens.font.sans}`, '&:hover': { backgroundColor: tokens.color.ink50 } }}>
                  {farmer === f.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>{f.name ?? f.code}{f.name && f.code ? <Box component="span" sx={{ color: 'text.secondary' }}> · {f.code}</Box> : null}</Box>
                  <Box sx={{ color: 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {f.plots} plots · {ha(f.areaHa)}{f.yieldT ? ` · produces ${t(f.yieldT)}` : ''}{f.attributedT !== undefined ? ` · supplies ${t(f.attributedT)}` : ''}
                  </Box>
                  <PlotBar passed={f.passed} flagged={f.flagged} rejected={f.rejected} total={f.plots} width={60} />
                </Box>
                {farmer === f.id && <Plots originId={originId} farmerId={f.id} />}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

function Plots({ originId, farmerId }: { originId: string; farmerId: string }) {
  const navigate = useNavigate()
  const { data, isPending } = useSupplyPlots(originId, { farmer: farmerId }, true)
  if (isPending) return <Typography variant="caption" sx={{ color: 'text.secondary', ml: 5 }}>Loading plots…</Typography>
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, ml: 5, mb: 1 }}>
      {data?.plots.map((p) => (
        <Box component="li" key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.75, font: `400 12px ${tokens.font.sans}` }}>
          <Box component="span" sx={{ width: 8, height: 8, borderRadius: '2px', backgroundColor: PLOT_COLOUR[p.status], flex: 'none' }} />
          <Box sx={{ fontFamily: tokens.font.mono, minWidth: 80 }}>{p.plotRef ?? p.id.slice(0, 8)}</Box>
          <Box sx={{ color: 'text.secondary', minWidth: 70 }}>{ha(p.areaHa)}</Box>
          <Box sx={{ color: 'text.secondary', minWidth: 90 }}>{p.yieldKg === null || p.yieldKg === undefined ? 'no production declared' : `${t(p.yieldKg / 1000)} a year`}</Box>
          {p.attributedT !== undefined && <Box sx={{ color: 'text.secondary', minWidth: 70 }}>→ {t(p.attributedT)}</Box>}
          <Box sx={{ color: PLOT_COLOUR[p.status], fontWeight: 500, minWidth: 64, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {p.status === 'rejected' && <CircleAlert size={11} strokeWidth={2} />}{LABEL[p.status]}
          </Box>
          <Button size="small" variant="text" endIcon={<ExternalLink size={12} />} onClick={() => navigate(`/plots/${p.id}`)} sx={{ height: 24, px: 1.5, fontSize: 12 }}>
            Inspect
          </Button>
        </Box>
      ))}
      {data && data.total > data.plots.length && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Showing {data.plots.length} of {data.total} plots.</Typography>
      )}
    </Box>
  )
}
